// Shared image-URL builder — the single source of truth for turning a stored
// image path/storage-key into a servable URL.
//
// Previously triplicated (apps/marketplace, apps/pro-sites, apps/portal) with
// SECURITY-relevant drift: pro-sites allowlists external hosts (untrusted
// pro-supplied keys rendered into that pro's public site — an arbitrary
// external URL would act as a visitor-exfiltration beacon), while the
// marketplace deliberately passes external URLs through (seed logos / blog
// photos on Unsplash/Pexels; Cloudflare Image Resizing rejects external source
// origins with 403, so they must be served raw). Both policies are correct for
// their surface — the policy is therefore an EXPLICIT parameter here, and each
// app keeps a thin wrapper that pins its own policy.

export interface ImageResizeParams {
	w?: number;
	h?: number;
	f?: string;
	q?: number;
	fit?: string;
}

export type ExternalUrlPolicy =
	| { kind: "passthrough" }
	| { kind: "allowlist"; hosts: ReadonlySet<string> };

export interface BuildImageUrlOptions {
	/** Origin that serves `/api/images/*` (fallback when no imageUrl). */
	apiUrl?: string;
	/** Dedicated image domain (e.g. images.interioring.com). Takes precedence. */
	imageUrl?: string;
	/** Cloudflare Image Resizing params — only applied when imageUrl is set. */
	resizeParams?: ImageResizeParams;
	/** What to do with full http(s) URLs. Default: passthrough. */
	external?: ExternalUrlPolicy;
}

/**
 * Build a full image URL from a storage key or path.
 * Handles both `/api/images/…`-prefixed paths and bare storage keys.
 */
export function buildImageUrl(
	path: string,
	options: BuildImageUrlOptions = {},
): string {
	if (!path) return "";
	const { apiUrl, imageUrl, resizeParams, external } = options;

	// Full URLs: apply the caller's external-origin policy.
	if (path.startsWith("http://") || path.startsWith("https://")) {
		if (!external || external.kind === "passthrough") return path;
		try {
			const { hostname } = new URL(path);
			if (external.hosts.has(hostname)) return path;
		} catch {
			// malformed URL — fall through to empty
		}
		return "";
	}

	if (imageUrl) {
		// Dedicated image domain — strip the /api/images/ prefix if present.
		const storageKey = path.startsWith("/api/images/")
			? path.slice("/api/images/".length)
			: path;

		if (resizeParams) {
			const parts: string[] = [];
			if (resizeParams.w) parts.push(`w=${resizeParams.w}`);
			if (resizeParams.h) parts.push(`h=${resizeParams.h}`);
			if (resizeParams.f) parts.push(`f=${resizeParams.f}`);
			if (resizeParams.q) parts.push(`q=${resizeParams.q}`);
			if (resizeParams.fit) parts.push(`fit=${resizeParams.fit}`);
			if (parts.length > 0) {
				return `${imageUrl}/cdn-cgi/image/onerror=redirect,${parts.join(",")}/${storageKey}`;
			}
		}

		return `${imageUrl}/${storageKey}`;
	}

	if (!apiUrl) return path;

	// API-served images.
	if (path.startsWith("/api/images/")) {
		return `${apiUrl}${path}`;
	}
	return `${apiUrl}/api/images/${path}`;
}
